import React, { useState, useEffect, useCallback } from 'react';
import { Play, Wrench, AlertCircle, Clock } from 'lucide-react';
import { syncAPI } from '../api';
import { Card, Button, PageHeader, Alert, StatCard } from '../components/UI';

export default function SyncStatus() {
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [running, setRunning] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [st, pd] = await Promise.allSettled([syncAPI.status(), syncAPI.pending()]);
      if (st.status === 'fulfilled') setStatus(st.value);
      if (pd.status === 'fulfilled') setPending(pd.value);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleRun = async () => {
    setRunning(true);
    try { await syncAPI.run(); setSuccess('Sync dimulai di background.'); setTimeout(loadStatus, 2000); }
    catch (e) { setError(e.message); }
    finally { setRunning(false); }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try { await syncAPI.reconcile({}); setSuccess('Reconciliation dimulai di background.'); }
    catch (e) { setError(e.message); }
    finally { setReconciling(false); }
  };

  return (
    <div className="animate-in">
      <PageHeader
        title="Sync Status"
        subtitle="Monitor sinkronisasi SQLite ↔ MySQL RADIUS"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={handleReconcile} loading={reconciling}><Wrench size={13} /> Reconcile</Button>
            <Button onClick={handleRun} loading={running}><Play size={13} /> Run Sync</Button>
          </div>
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard label="Users Pending" value={loading ? '…' : pending?.users?.pending ?? 0} icon={Clock} color="var(--accent-orange)" />
        <StatCard label="Users Error" value={loading ? '…' : pending?.users?.error ?? 0} icon={AlertCircle} color="var(--accent-red)" />
        <StatCard label="NAS Pending" value={loading ? '…' : pending?.nas?.pending ?? 0} icon={Clock} color="var(--accent-orange)" />
        <StatCard label="NAS Error" value={loading ? '…' : pending?.nas?.error ?? 0} icon={AlertCircle} color="var(--accent-red)" />
      </div>

      {/* Scheduler status */}
      {status && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)' }}>Scheduler Status</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {Object.entries(status).map(([k, v]) => (
              <div key={k} style={{ padding: '8px 14px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{k}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--text-primary)' }}>{String(v)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
